import { Icon } from '@ps/isp-web-component-library';
import { BetBuilderBadge } from '@ps/isp-web-component-library/icons/BetBuilderBadge';
import { LiveStreamingBadge } from '@ps/isp-web-component-library/icons/LiveStreamingBadge';
import { PriceBoost } from '@ps/isp-web-component-library/icons/PriceBoost';
import { StarSubIcon } from '@ps/isp-web-component-library/icons/StarSubIcon';
import type {
  EventId,
  EventLevelCompoundMarketTemplateColumnClientConfig,
  EventType,
  MarketId,
  MarketLevelCompoundMarketTemplateColumnClientConfig,
  MarketType,
  MarketView,
  MeetingBasedRunner,
  Runner
} from '@ps/isp-web-services-types';
import { RunnerStatusMarketDetails } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import { notEmpty } from '@ps/web-core-utils';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { groupBy, orderBy, unzip, values } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { RootState } from '@swf/core/store';
import { Selection, SportsLink } from '@swf/sports/components';
import EventStatus from '@swf/sports/components/EventStatus/EventStatus';
import useEventStatus from '@swf/sports/components/EventStatus/hooks/useEventStatus';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import SingleParticipantScoreboard from '@swf/sports/components/Scoreboard/SingleParticipantScoreboard';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import { isValidInPlayStatus } from '@swf/sports/utils/isValidInPlayStatus';
import useIsEventTypeLiveDataEnabled from '@swf/sports/utils/useIsEventTypeLiveDataEnabled';

import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';

import {
  TXTCLI_First,
  TXTCLI_Last,
  TXTCLI_Next,
  TXTWCL_1X_3,
  TXTWCL_2X_2,
  TXTWCL_3X_1,
  TXTWCL_Anytime,
  TXTWCL_Bet_Builder,
  TXTWCL_Game_Lines,
  TXTWCL_Live_Stream,
  TXTWCL_Own_goals_do_not_count,
  TXTWCL_Show_More,
  TXTWCL_Star_Sub_enabled_selection,
  TXTWCL_odds_boost_selections
} from './TableColumnTable.messages.json';
import styles from './TableColumnTable.scss';

const translationMap: Partial<Record<string, TranslationTag>> = {
  ISP_I18N_ANYTIME: TXTWCL_Anytime,
  ISP_I18N_FIRST: TXTCLI_First,
  ISP_I18N_LAST: TXTCLI_Last,
  ISP_I18N_NEXT: TXTCLI_Next,
  ISP_I18N_OWN_GOALS_DO_NOT_COUNT: TXTWCL_Own_goals_do_not_count,
  ISP_I18N_GAME_LINES: TXTWCL_Game_Lines,
  ISP_I18N_ONE_OR_MORE: TXTWCL_1X_3,
  ISP_I18N_TWO_OR_MORE: TXTWCL_2X_2,
  ISP_I18N_THREE_OR_MORE: TXTWCL_3X_1
};

const SHOW_MORE_THRESHOLD = 10;

type RunnerWithParentIds = (Runner | MeetingBasedRunner) & {
  marketId: MarketId;
  eventId: EventId;
  eventTypeId: EventType;
};

type ClientConfig =
  | MarketLevelCompoundMarketTemplateColumnClientConfig
  | EventLevelCompoundMarketTemplateColumnClientConfig;

export interface Props {
  marketIds: (MarketId | null)[];
  title: ClientConfig['title'];
  information?: ClientConfig['information'];
  marketTypeCodes: ClientConfig['marketTypeCodes'];
  shouldCollapseRunners?: ClientConfig['shouldCollapseRunners'];
  columnTitles?: ClientConfig['columnTitles'];
  isHeaderVisible?: boolean;
  displayLevel?: 'MARKET' | 'EVENT';
}

export const groupRunners = (
  markets: (MarketView | undefined)[],
  marketTypeCodes: MarketType[],
  shouldCollapseRunners?: true
) => {
  const orderedCollapsedMarkets = marketTypeCodes.map((marketType) =>
    markets.find((market) => market?.marketType === marketType)
  );
  const orderedFilteredMarkets = marketTypeCodes
    .map((marketType) => markets.find((market) => market?.marketType === marketType))
    .filter(notEmpty);

  const allRunners = orderedFilteredMarkets.flatMap(({ eventId, eventTypeId, marketId, runners }) =>
    runners.map<RunnerWithParentIds>((runner) => ({
      ...runner,
      eventId,
      eventTypeId,
      marketId
    }))
  );

  const runnerRows = shouldCollapseRunners
    ? unzip(
        orderedCollapsedMarkets.map((market) =>
          allRunners.filter((runner) => runner.marketId === market?.marketId)
        )
      )
    : values(groupBy(allRunners, 'selectionId')).map((runners) =>
        orderedFilteredMarkets.map(({ marketId }) =>
          runners.find((runner) => runner.marketId === marketId)
        )
      );

  return shouldCollapseRunners
    ? runnerRows
    : orderBy(runnerRows, (runners) =>
        // Order by sortPriority of most significant market (first in runners array)
        // If runner is suspended, use infinity to order to ensure it is last in list
        runners[0]?.runnerStatus === RunnerStatusMarketDetails.SUSPENDED
          ? Number.MAX_SAFE_INTEGER
          : runners[0]?.sortPriority
      );
};

const TableColumnTable = ({
  marketIds,
  information,
  marketTypeCodes,
  shouldCollapseRunners,
  columnTitles,
  isHeaderVisible = true,
  displayLevel = 'MARKET',
  title
}: Props) => {
  const { formatMessage } = useRegulatoryIntl();
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const allMarkets = useSelector(widgetSelect.markets);
  const events = useSelector(widgetSelect.events);
  const isBetBuilderEnabled = useFeature(FeatureFlag.ISPBetBuilder);

  const markets = useMemo(() => {
    const mktIds = new Set(marketIds.filter(notEmpty));
    return Object.values(allMarkets).filter((market) => market && mktIds.has(market.marketId));
  }, [allMarkets, marketIds]);

  const hasBetBuilderMarkets =
    isBetBuilderEnabled && markets.find((market) => market?.isSgmEligible);

  const tableRows = groupRunners(markets, marketTypeCodes, shouldCollapseRunners);
  const [firstMarket] = markets.filter(notEmpty);
  const firstMarketEventId = firstMarket.eventId;
  const event = events[firstMarketEventId];

  const eventType = useSelector((state: RootState) =>
    widgetSelect.eventType(state, event.eventTypeId)
  );

  const { isError: isScoreboardError } = useLiveData();

  const eventStatus = useEventStatus({
    inPlay: firstMarket.inPlay,
    startTime: event.eventStartTime,
    canTurnInPlay: firstMarket.offeredInPlay
  });

  const isScoreboardAvailable =
    useIsEventTypeLiveDataEnabled(eventType.eventTypeId) &&
    !isScoreboardError &&
    isValidInPlayStatus(eventStatus);

  const marketHeaders = marketTypeCodes
    .map((code) => Object.values(allMarkets).find((market) => market?.marketType === code))
    .map((market) => {
      if (!market) return undefined;

      return {
        marketType: market.marketType,
        marketName: market.marketName,
        isStarSubEnabled: market.isStarSubEnabled
      };
    });

  const [rowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    tableRows,
    firstMarket.marketId,
    SHOW_MORE_THRESHOLD
  );

  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );
  const { oddsBoostCount, hasLiveStreamAvailable } = event;

  const informationTranslation = information ? translationMap[information] : undefined;
  const titleTranslation = translationMap[title];

  return (
    <>
      <table data-testid="compound-table-column" className={styles.table}>
        {information && (
          <caption className={styles.information}>
            {informationTranslation ? <Translation {...informationTranslation} /> : null}
          </caption>
        )}
        <thead
          aria-hidden={!isHeaderVisible}
          className={classNames({ [styles.hidden]: !isHeaderVisible })}
          data-testid="market-template-header"
        >
          <tr>
            {titleTranslation && displayLevel === 'EVENT' ? (
              <th>
                <Translation {...titleTranslation} />
              </th>
            ) : (
              <td />
            )}
            {marketHeaders.map((header, i) => {
              if (!header && !shouldCollapseRunners) return null;
              if (!header) return <th scope="col" key={`empty-header-${i}`} />;
              const { marketType, marketName, isStarSubEnabled } = header;
              const title = columnTitles && columnTitles[marketType];
              const subTitleTranslation = title ? translationMap[title] : undefined;
              return (
                <th scope="col" key={marketName}>
                  {subTitleTranslation ? (
                    <Translation {...subTitleTranslation} />
                  ) : title ? (
                    title
                  ) : (
                    marketName
                  )}
                  {isStarSubEnabled ? (
                    <Icon
                      icon={StarSubIcon}
                      className={styles.starSubIcon}
                      label={formatMessage(TXTWCL_Star_Sub_enabled_selection)}
                      size="xs"
                    />
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rowsToDisplay.map((row, i) => {
            const { runnerName, eventId, marketId, selectionId } = row.find(Boolean) ?? {};
            const market = allMarkets[marketId ?? ''];

            if (!runnerName || !eventId || !market) return null;

            const enrichedParticipant = events[eventId].enrichedParticipants?.[i];

            const rowName = shouldCollapseRunners ? enrichedParticipant?.participant : runnerName;

            return (
              <tr
                key={`${eventId}-${selectionId}`}
                className={classNames({ [styles.eventTableRow]: displayLevel === 'EVENT' })}
              >
                <th scope="row">
                  {displayLevel === 'EVENT' ? (
                    <SportsLink
                      className={styles.team}
                      alias="sportsEvent"
                      values={{
                        eventTypeSlug: eventType.eventTypeSlug,
                        eventTypeId: eventType.eventTypeId,
                        competitionSlug: competition.competitionSlug,
                        competitionId: competition.competitionId,
                        eventSlug: event.eventSlug,
                        eventId: event.eventId
                      }}
                    >
                      {isScoreboardAvailable ? (
                        <SingleParticipantScoreboard
                          eventTypeId={event.eventTypeId}
                          eventId={event.eventId}
                          enrichedParticipant={enrichedParticipant}
                        />
                      ) : (
                        rowName
                      )}
                    </SportsLink>
                  ) : (
                    rowName
                  )}
                </th>
                {row.map((runner, j) => {
                  if (typeof runner === 'undefined') {
                    return (
                      <td key={`cell-${rowName}-${i}-${j}`}>
                        <span className={styles.emptySelection}>-</span>
                      </td>
                    );
                  }

                  const market = markets.find((market) => market?.marketId === runner.marketId);

                  if (!market) return null;

                  return (
                    <td key={`${runner.selectionId}-${j}`}>
                      <Selection
                        competitionId={market.competitionId}
                        eventId={market.eventId}
                        eventTypeId={market.eventTypeId}
                        handicap={runner.handicap}
                        legTypes={market.legTypes}
                        marketId={market.marketId}
                        marketStatus={market.marketStatus}
                        marketType={market.marketType}
                        odds={runner.winRunnerOdds}
                        runnerResultType={runner.resultType}
                        runnerStatus={runner.runnerStatus}
                        selectionId={runner.selectionId}
                        startTime={events[market.eventId].eventStartTime}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {displayLevel === 'EVENT' ? (
          <tfoot>
            <tr>
              {firstMarket.inPlay ? (
                <td className={styles.eventTime}>
                  <EventStatus
                    isInPlay={firstMarket.inPlay}
                    startTime={event.eventStartTime}
                    isTurnInPlay={firstMarket.offeredInPlay}
                    eventId={event.eventId}
                    eventTypeId={event.eventTypeId}
                  />
                </td>
              ) : (
                <td className={styles.eventTime}>
                  <FormattedDateTime date={event.eventStartTime} />
                </td>
              )}

              <td className={styles.icons}>
                {oddsBoostCount ? (
                  <Icon
                    icon={PriceBoost}
                    label={formatMessage(TXTWCL_odds_boost_selections)}
                    size="sm"
                  />
                ) : null}

                {hasBetBuilderMarkets ? (
                  <Icon
                    icon={BetBuilderBadge}
                    label={formatMessage(TXTWCL_Bet_Builder)}
                    size="sm"
                  />
                ) : null}

                {hasLiveStreamAvailable ? (
                  <Icon
                    icon={LiveStreamingBadge}
                    label={formatMessage(TXTWCL_Live_Stream)}
                    size="sm"
                  />
                ) : null}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
      {limitRows && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={handleShowMoreClick}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

export default TableColumnTable;
